import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";

import { Box, debounce, Stack, Typography } from "@mui/material";
import { AsyncContentMui, RoleComponentGuard, Search } from "components";
import { AccessDenied } from "components/Access-denied/AccessDenied";
import { useNavigate } from "hooks/shared-hooks/use-navigate";
import { useActionHook } from "hooks";
import { useUserManageActions } from "store";
import { IContact, IContactsQueries } from "store/types/user-management";

import { RoleName } from "../../../constants";
import { ContactModal } from "./ContactModal/ContactModal";
import { Filters } from "./Filters";
import { Table } from "./Table/Table";

const CONTACTS_ALLOWED_ROLES = [RoleName.Admin, RoleName.Analyst, RoleName.TeamViewer];

const cardSx = {
  bgcolor: "white",
  borderRadius: "8px",
  boxShadow: "0px 1px 2px rgba(18, 18, 23, 0.1)",
  p: "16px"
} as const;

const emptyTitleSx = { fontSize: "18px", fontWeight: 500, color: "#0a3142", mb: "4px" } as const;
const emptySubtitleSx = { fontSize: "14px", color: "#898989", m: 0 } as const;

export const Contacts: React.FC = () => {
  const { getContactsAction } = useUserManageActions();
  const { data, loading, error, action } = useActionHook(getContactsAction);
  const navigate = useNavigate();

  const [queries, setQueries] = useState<IContactsQueries>({ page: 1, per_page: 50 });
  const [openedContact, setOpenedContact] = useState<IContact | null>(null);
  const isInitialLoadStarted = useRef(false);

  // Filter/search changes reset to page 1; page changes preserve the rest of the query.
  const updateQueries = useCallback((partial: Partial<IContactsQueries>) => {
    setQueries((prev) => ({ ...prev, ...partial, page: 1 }));
  }, []);

  const onSearch = useCallback((search: string) => updateQueries({ search }), [updateQueries]);

  // Stabilize the debounced handler so its timer survives re-renders.
  const onSearchDebounce = useMemo(() => debounce(onSearch, 500), [onSearch]);
  useEffect(() => () => onSearchDebounce.clear(), [onSearchDebounce]);

  const handleChangePage = useCallback((newPage: number) => {
    setQueries((prev) => ({ ...prev, page: newPage + 1 })); // TablePagination is 0-based
  }, []);

  useEffect(() => {
    action(queries);
  }, [queries, action]);

  useEffect(() => {
    if (loading) isInitialLoadStarted.current = true;
  }, [loading]);

  const total = data?.total ?? 0;
  const hasActiveFilters = useMemo(
    () => Boolean(queries.search || queries.deal_id || queries.firm),
    [queries.search, queries.deal_id, queries.firm]
  );
  const hasLoadedOnce = isInitialLoadStarted.current || Boolean(data);
  const isError = Boolean(error);
  const isLoadedWithData = hasLoadedOnce && !loading && total > 0;
  const isEmpty = hasLoadedOnce && !loading && total === 0;

  return (
    <RoleComponentGuard
      availableRoles={CONTACTS_ALLOWED_ROLES}
      RenderInstead={<AccessDenied redirectClick={() => navigate({ route: "main" })} title="Back home" />}
    >
      <Stack sx={{ gap: "15px", flexGrow: 1, overflow: "hidden", height: "100%", width: "100%" }}>
        <Stack
          direction="row"
          sx={{ ...cardSx, justifyContent: "space-between", alignItems: "center", gap: "10px" }}
        >
          <Filters queries={queries} onChange={updateQueries} />
          <Box sx={{ "& input": { height: "30px" }, "& input::placeholder": { fontSize: "16px" } }}>
            <Search
              defaultValue={queries.search || ""}
              onChange={(e) => onSearchDebounce(e.target.value)}
              placeholder="Search"
            />
          </Box>
        </Stack>

        <Stack sx={{ ...cardSx, flexGrow: 1, overflow: "hidden", minHeight: 0 }}>
          <AsyncContentMui
            spinnerProps={{ size: 150 }}
            isLoading={loading}
            boxProps={{
              sx: {
                overflow: "hidden",
                height: "100%",
                flexGrow: 1,
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center"
              }
            }}
          >
            {isError ? (
              <Stack sx={{ flexGrow: 1 }} alignItems="center" justifyContent="center">
                <Typography sx={emptyTitleSx}>Couldn&apos;t load contacts.</Typography>
              </Stack>
            ) : isEmpty ? (
              <Stack sx={{ flexGrow: 1 }} alignItems="center" justifyContent="center">
                <Typography sx={emptyTitleSx}>
                  {hasActiveFilters ? "No contacts match your filters." : "No contacts yet."}
                </Typography>
                {hasActiveFilters && (
                  <Typography sx={emptySubtitleSx}>Try adjusting your search or filters.</Typography>
                )}
              </Stack>
            ) : isLoadedWithData && data ? (
              <Table
                data={data}
                page={queries.page!}
                rowsPerPage={queries.per_page!}
                handleChangePage={handleChangePage}
                onSelectContact={setOpenedContact}
              />
            ) : null}
          </AsyncContentMui>
        </Stack>
      </Stack>

      <ContactModal
        open={Boolean(openedContact)}
        contact={openedContact}
        onClose={() => setOpenedContact(null)}
      />
    </RoleComponentGuard>
  );
};
